"use client";
import Link from "next/link";
import {
	MapPin,
	Wallet,
	Search,
	Circle,
	ArrowRight,
	Download,
	CreditCard,
	Shield,
	Smartphone,
	Navigation,
	Eye,
	Coins,
	Users,
	Globe,
	Zap,
	CheckCircle,
} from "lucide-react";

export default function StartGuide() {
	return (
		<div className="min-h-full bg-mgt-black">
			{/* Hero Section */}
			<section className="relative px-4 py-16 md:py-24">
				<div className="max-w-6xl mx-auto text-center">
					<h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
						Start Your
						<span className="block text-mgt-green">Geospatial Adventure</span>
					</h1>
					<p className="text-lg md:text-xl text-off-white max-w-3xl mx-auto leading-relaxed mb-8">
						Learn how to drop, discover, and snag location-based NFTs in the
						real world. Your complete guide to navigating the MyGeoTokens
						ecosystem.
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Link
							href="/wallet"
							className="bg-mgt-green text-mgt-black px-8 py-3 rounded-full font-semibold hover:bg-mgt-yellow transition-colors duration-200"
						>
							Get Started Now
						</Link>
						<Link
							href="/about"
							className="border border-mgt-green text-mgt-green px-8 py-3 rounded-full font-semibold hover:bg-mgt-green hover:text-mgt-black transition-colors duration-200"
						>
							Learn More
						</Link>
					</div>
				</div>
			</section>

			{/* Quick Start Overview */}
			<section className="px-4 py-16 md:py-20 bg-brown/20">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
							Three Simple Steps
						</h2>
						<p className="text-off-white text-lg">
							MyGeoTokens revolutionizes NFTs by connecting them to real-world
							locations
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
						{/* Step 1: Fence */}
						<div className="text-center space-y-6">
							<div className="bg-brown rounded-2xl p-8 md:p-12 hover:bg-brown/80 transition-colors duration-300">
								<div className="w-20 h-20 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-6">
									<Circle className="w-10 h-10 text-mgt-black" />
								</div>
								<h3 className="text-2xl font-bold text-mgt-green mb-4">
									1. Fence
								</h3>
								<p className="text-off-white leading-relaxed">
									Create geofenced boundaries around real-world locations.
									Define exactly where collectors can discover your NFTs.
								</p>
							</div>
						</div>

						{/* Step 2: Drop */}
						<div className="text-center space-y-6">
							<div className="bg-brown rounded-2xl p-8 md:p-12 hover:bg-brown/80 transition-colors duration-300">
								<div className="w-20 h-20 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-6">
									<Download className="w-10 h-10 text-mgt-black" />
								</div>
								<h3 className="text-2xl font-bold text-mgt-green mb-4">
									2. Drop
								</h3>
								<p className="text-off-white leading-relaxed">
									Deploy your NFT collection to the Solana blockchain and drop
									them within your geofenced area with custom pricing.
								</p>
							</div>
						</div>

						{/* Step 3: Snag */}
						<div className="text-center space-y-6">
							<div className="bg-brown rounded-2xl p-8 md:p-12 hover:bg-brown/80 transition-colors duration-300">
								<div className="w-20 h-20 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-6">
									<Search className="w-10 h-10 text-mgt-black" />
								</div>
								<h3 className="text-2xl font-bold text-mgt-green mb-4">
									3. Snag
								</h3>
								<p className="text-off-white leading-relaxed">
									Collectors visit the location, enter the geofenced area, and
									snag available NFTs in real-time.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Getting Started Section */}
			<section className="px-4 py-16 md:py-20">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
							Getting Started
						</h2>
						<p className="text-off-white text-lg">
							Everything you need to begin your geospatial NFT journey
						</p>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
						{/* Prerequisites */}
						<div className="bg-brown rounded-2xl p-8">
							<h3 className="text-2xl font-bold text-mgt-green mb-6 flex items-center gap-3">
								<Shield className="w-6 h-6" />
								Prerequisites
							</h3>
							<div className="space-y-4">
								<div className="flex items-start gap-4">
									<Wallet className="w-6 h-6 text-mgt-green mt-1 flex-shrink-0" />
									<div>
										<h4 className="text-white font-semibold mb-1">
											Solana Wallet
										</h4>
										<p className="text-off-white text-sm">
											Use MGT Wallet, Solflare, or another
											Solana-compatible wallet
										</p>
									</div>
								</div>
								<div className="flex items-start gap-4">
									<Coins className="w-6 h-6 text-mgt-green mt-1 flex-shrink-0" />
									<div>
										<h4 className="text-white font-semibold mb-1">
											SOL for Fees
										</h4>
										<p className="text-off-white text-sm">
											Small amount of SOL needed for transaction fees (usually
											&lt; $0.01)
										</p>
									</div>
								</div>
								<div className="flex items-start gap-4">
									<Smartphone className="w-6 h-6 text-mgt-green mt-1 flex-shrink-0" />
									<div>
										<h4 className="text-white font-semibold mb-1">
											Location Access
										</h4>
										<p className="text-off-white text-sm">
											Enable location services for geofencing features
										</p>
									</div>
								</div>
								<div className="flex items-start gap-4">
									<Globe className="w-6 h-6 text-mgt-green mt-1 flex-shrink-0" />
									<div>
										<h4 className="text-white font-semibold mb-1">
											Web3 Browser
										</h4>
										<p className="text-off-white text-sm">
											Use a modern browser with Web3 capabilities
										</p>
									</div>
								</div>
							</div>
						</div>

						{/* Account Setup */}
						<div className="bg-brown rounded-2xl p-8">
							<h3 className="text-2xl font-bold text-mgt-green mb-6 flex items-center gap-3">
								<Users className="w-6 h-6" />
								Account Setup
							</h3>
							<div className="space-y-6">
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2">
										1. Create Account
									</h4>
									<p className="text-off-white text-sm">
										Sign up with email and verify your account via OTP
									</p>
								</div>
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2">
										2. Connect Wallet
									</h4>
									<p className="text-off-white text-sm">
										Link your Solana wallet to enable NFT transactions
									</p>
								</div>
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2">
										3. Complete Profile
									</h4>
									<p className="text-off-white text-sm">
										Set up your profile with location and preferences
									</p>
								</div>
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2">
										4. Enable Location
									</h4>
									<p className="text-off-white text-sm">
										Grant location permissions for geofencing features
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* For Creators Section */}
			<section className="px-4 py-16 md:py-20 bg-brown/20">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
							For NFT Creators
						</h2>
						<p className="text-off-white text-lg">
							Learn how to drop location-based NFT collections
						</p>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div className="space-y-8">
							<div className="flex items-start gap-4">
								<div className="w-8 h-8 bg-mgt-green rounded-full flex items-center justify-center flex-shrink-0">
									<span className="text-mgt-black font-bold">1</span>
								</div>
								<div>
									<h3 className="text-xl font-bold text-white mb-2">
										Prepare Your NFTs
									</h3>
									<p className="text-off-white">
										Upload your NFT collection to Solana. You need at least 2
										NFTs to create a drop. Both standard and compressed NFTs are
										supported.
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="w-8 h-8 bg-mgt-green rounded-full flex items-center justify-center flex-shrink-0">
									<span className="text-mgt-black font-bold">2</span>
								</div>
								<div>
									<h3 className="text-xl font-bold text-white mb-2">
										Select NFTs for Drop
									</h3>
									<p className="text-off-white">
										Choose which NFTs to include in your geospatial drop. Set
										pricing and add descriptive tags for better discoverability.
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="w-8 h-8 bg-mgt-green rounded-full flex items-center justify-center flex-shrink-0">
									<span className="text-mgt-black font-bold">3</span>
								</div>
								<div>
									<h3 className="text-xl font-bold text-white mb-2">
										Create Geofence
									</h3>
									<p className="text-off-white">
										Draw a boundary around your chosen location. Collectors must
										be within this area to purchase your NFTs.
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="w-8 h-8 bg-mgt-green rounded-full flex items-center justify-center flex-shrink-0">
									<span className="text-mgt-black font-bold">4</span>
								</div>
								<div>
									<h3 className="text-xl font-bold text-white mb-2">
										Deploy & Earn
									</h3>
									<p className="text-off-white">
										Your NFTs appear on the map for collectors to discover. Earn
										80% of each sale, with 20% going to platform fees.
									</p>
								</div>
							</div>
						</div>

						<div className="bg-brown rounded-2xl p-8">
							<h3 className="text-2xl font-bold text-mgt-green mb-6">
								Creator Benefits
							</h3>
							<div className="space-y-4">
								<div className="flex items-center gap-3">
									<CheckCircle className="w-5 h-5 text-mgt-green flex-shrink-0" />
									<span className="text-off-white">
										Location-based marketing
									</span>
								</div>
								<div className="flex items-center gap-3">
									<CheckCircle className="w-5 h-5 text-mgt-green flex-shrink-0" />
									<span className="text-off-white">Real-world engagement</span>
								</div>
								<div className="flex items-center gap-3">
									<CheckCircle className="w-5 h-5 text-mgt-green flex-shrink-0" />
									<span className="text-off-white">80% revenue share</span>
								</div>
								<div className="flex items-center gap-3">
									<CheckCircle className="w-5 h-5 text-mgt-green flex-shrink-0" />
									<span className="text-off-white">Low transaction costs</span>
								</div>
								<div className="flex items-center gap-3">
									<CheckCircle className="w-5 h-5 text-mgt-green flex-shrink-0" />
									<span className="text-off-white">Instant settlements</span>
								</div>
								<div className="flex items-center gap-3">
									<CheckCircle className="w-5 h-5 text-mgt-green flex-shrink-0" />
									<span className="text-off-white">Global accessibility</span>
								</div>
							</div>
							<Link
								href="/drop-nft"
								className="w-full bg-mgt-green text-mgt-black font-semibold py-3 rounded-full hover:bg-mgt-yellow transition-colors duration-200 flex items-center justify-center gap-2 mt-6"
							>
								Start Dropping NFTs
								<ArrowRight className="w-5 h-5" />
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* For Collectors Section */}
			<section className="px-4 py-16 md:py-20">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
							For NFT Collectors
						</h2>
						<p className="text-off-white text-lg">
							Discover and snag location-based NFTs around the world
						</p>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
						<div className="bg-brown rounded-2xl p-8">
							<h3 className="text-2xl font-bold text-mgt-green mb-6">
								How to Snag NFTs
							</h3>
							<div className="space-y-6">
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2 flex items-center gap-2">
										<Search className="w-5 h-5 text-mgt-green" />
										Explore the Map
									</h4>
									<p className="text-off-white text-sm">
										Browse the interactive map to find NFT drops near you or in
										areas you plan to visit
									</p>
								</div>
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2 flex items-center gap-2">
										<Navigation className="w-5 h-5 text-mgt-green" />
										Visit the Location
									</h4>
									<p className="text-off-white text-sm">
										Travel to the real-world location where the NFTs are dropped
									</p>
								</div>
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2 flex items-center gap-2">
										<Eye className="w-5 h-5 text-mgt-green" />
										Enter the Geofence
									</h4>
									<p className="text-off-white text-sm">
										Step into the designated geofenced area to unlock purchase
										ability
									</p>
								</div>
								<div className="border-l-4 border-mgt-green pl-4">
									<h4 className="text-white font-semibold mb-2 flex items-center gap-2">
										<CreditCard className="w-5 h-5 text-mgt-green" />
										Snag Your NFT
									</h4>
									<p className="text-off-white text-sm">
										Complete the purchase with your connected wallet and receive
										your unique NFT
									</p>
								</div>
							</div>
						</div>

						<div className="space-y-8">
							<div className="flex items-start gap-4">
								<div className="w-8 h-8 bg-mgt-green rounded-full flex items-center justify-center flex-shrink-0">
									<MapPin className="w-5 h-5 text-mgt-black" />
								</div>
								<div>
									<h3 className="text-xl font-bold text-white mb-2">
										Location-Based Discovery
									</h3>
									<p className="text-off-white">
										Find unique NFTs tied to specific locations. Each drop has a
										story connected to its real-world setting.
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="w-8 h-8 bg-mgt-green rounded-full flex items-center justify-center flex-shrink-0">
									<Zap className="w-5 h-5 text-mgt-black" />
								</div>
								<div>
									<h3 className="text-xl font-bold text-white mb-2">
										Instant Ownership
									</h3>
									<p className="text-off-white">
										Fast, secure transactions on Solana blockchain. Your NFT is
										transferred immediately upon purchase.
									</p>
								</div>
							</div>

							<div className="flex items-start gap-4">
								<div className="w-8 h-8 bg-mgt-green rounded-full flex items-center justify-center flex-shrink-0">
									<Globe className="w-5 h-5 text-mgt-black" />
								</div>
								<div>
									<h3 className="text-xl font-bold text-white mb-2">
										Global Adventures
									</h3>
									<p className="text-off-white">
										Turn travel and exploration into treasure hunts. Collect
										NFTs from locations around the world.
									</p>
								</div>
							</div>

							<Link
								href="/"
								className="inline-flex items-center gap-2 bg-mgt-green text-mgt-black font-semibold px-6 py-3 rounded-full hover:bg-mgt-yellow transition-colors duration-200"
							>
								Explore NFT Map
								<ArrowRight className="w-5 h-5" />
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* Technical Details Section */}
			<section className="px-4 py-16 md:py-20 bg-brown/20">
				<div className="max-w-6xl mx-auto">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
							Technical Features
						</h2>
						<p className="text-off-white text-lg">
							Built on cutting-edge blockchain and geospatial technology
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						<div className="bg-brown rounded-2xl p-6 text-center">
							<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-4">
								<Shield className="w-8 h-8 text-mgt-black" />
							</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Solana Blockchain
							</h3>
							<p className="text-off-white text-sm">
								Fast, low-cost transactions with sub-second finality. Perfect
								for real-time NFT interactions.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6 text-center">
							<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-4">
								<Navigation className="w-8 h-8 text-mgt-black" />
							</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Advanced Geofencing
							</h3>
							<p className="text-off-white text-sm">
								Precise location verification using GPS and polygon geometry for
								secure area-based access control.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6 text-center">
							<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-4">
								<Wallet className="w-8 h-8 text-mgt-black" />
							</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Web3 Integration
							</h3>
							<p className="text-off-white text-sm">
								Seamless wallet connectivity with MGT Wallet and other
								popular Solana wallets like Solflare.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6 text-center">
							<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-4">
								<Eye className="w-8 h-8 text-mgt-black" />
							</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Real-time Updates
							</h3>
							<p className="text-off-white text-sm">
								Live map updates showing available NFTs, recent purchases, and
								collection status.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6 text-center">
							<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-4">
								<Download className="w-8 h-8 text-mgt-black" />
							</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Compressed NFTs
							</h3>
							<p className="text-off-white text-sm">
								Support for both standard and compressed NFTs, reducing storage
								costs and environmental impact.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6 text-center">
							<div className="w-16 h-16 bg-mgt-green rounded-full flex items-center justify-center mx-auto mb-4">
								<Smartphone className="w-8 h-8 text-mgt-black" />
							</div>
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Mobile Optimized
							</h3>
							<p className="text-off-white text-sm">
								Responsive design optimized for mobile exploration and on-the-go
								NFT collecting.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ Section */}
			<section className="px-4 py-16 md:py-20">
				<div className="max-w-4xl mx-auto">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
							Quick Answers
						</h2>
						<p className="text-off-white text-lg">
							Common questions about getting started with MyGeoTokens
						</p>
					</div>

					<div className="space-y-6">
						<div className="bg-brown rounded-2xl p-6">
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								How much does it cost to use MyGeoTokens?
							</h3>
							<p className="text-off-white">
								The platform is free to use. You only pay Solana network fees
								(typically less than $0.01) for transactions and the price of
								NFTs you purchase. Creators pay a 20% platform fee on sales.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6">
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Do I need to be at the exact location to buy an NFT?
							</h3>
							<p className="text-off-white">
								Yes, you must be within the geofenced area drawn by the creator
								to purchase their NFTs. This is what makes MyGeoTokens unique -
								it connects digital assets to real-world locations.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6">
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								What wallets are supported?
							</h3>
							<p className="text-off-white">
								Any Solana-compatible wallet works, including MGT Wallet,
								Solflare, and other Web3 wallets that support the Solana
								blockchain network.
							</p>
						</div>

						<div className="bg-brown rounded-2xl p-6">
							<h3 className="text-xl font-bold text-mgt-green mb-3">
								Can I sell NFTs I&apos;ve collected?
							</h3>
							<p className="text-off-white">
								Once you own an NFT from MyGeoTokens, you can trade it on any
								Solana NFT marketplace like Magic Eden, OpenSea, or Tensor. Your
								NFTs are fully transferable.
							</p>
						</div>
					</div>

					<div className="text-center mt-12">
						<Link
							href="/faqs"
							className="inline-flex items-center gap-2 bg-mgt-green text-mgt-black font-semibold px-6 py-3 rounded-full hover:bg-mgt-yellow transition-colors duration-200"
						>
							View All FAQs
							<ArrowRight className="w-5 h-5" />
						</Link>
					</div>
				</div>
			</section>

			{/* Call to Action Section */}
			<section className="px-4 py-16 md:py-20 bg-brown/20">
				<div className="max-w-4xl mx-auto text-center">
					<h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
						Ready to Start Your Journey?
					</h2>
					<p className="text-lg text-off-white mb-8">
						Join thousands of creators and collectors exploring the future of
						location-based NFTs
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Link
							href="/wallet"
							className="bg-mgt-green text-mgt-black px-8 py-4 rounded-full font-semibold hover:bg-mgt-yellow transition-colors duration-200 flex items-center justify-center gap-2"
						>
							Create Account
							<ArrowRight className="w-5 h-5" />
						</Link>
						<Link
							href="/"
							className="border border-mgt-green text-mgt-green px-8 py-4 rounded-full font-semibold hover:bg-mgt-green hover:text-mgt-black transition-colors duration-200 flex items-center justify-center gap-2"
						>
							Explore Map
							<MapPin className="w-5 h-5" />
						</Link>
					</div>
				</div>
			</section>
		</div>
	);
}
